import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { communityAPI } from "../../services/api.js";
import { customAgentAPI, agentAPI } from "../../services/api.js";
import { getUserIdFromToken } from '../../utils/auth.js';

/* ─── Design tokens ─────────────────────────────────────────────── */
const T = {
  green: "var(--lyra-color-success)",
  greenDark: "#184C3A",
  greenDeep: "var(--lyra-color-text)",
  greenLight: "var(--lyra-color-surface-2)",
  border: "var(--lyra-color-border)",
  bg: "var(--lyra-color-surface-2)",
  white: "var(--lyra-color-surface)",
  ink: "var(--lyra-color-text)",
  ink2: "var(--app-text-2)",
  ink4: "var(--lyra-color-text-muted)",
  ink6: "var(--app-text-3)",
};

/* ─── Helpers ────────────────────────────────────────────────────── */
function formatDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function timeAgo(iso) {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins} min${mins !== 1 ? "s" : ""} ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} hour${hours !== 1 ? "s" : ""} ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days !== 1 ? "s" : ""} ago`;
}

/* ─── LyranAvatar ────────────────────────────────────────────────── */
function LyranAvatar({ src, name = "", size = 96 }) {
  const initials =
    (name || "")
      .split(" ")
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "✦";
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: "50%",
        padding: 4,
        background:
          "linear-gradient(135deg, rgba(255,255,255,0.95), rgba(255,255,255,0.5))",
        boxShadow: "0 16px 40px rgba(0,0,0,0.18)",
        position: "relative",
        flexShrink: 0,
      }}
    >
      {src ? (
        <img
          src={src}
          alt={name}
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            objectFit: "cover",
          }}
        />
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            borderRadius: "50%",
            background: "linear-gradient(135deg,var(--lyra-color-success),var(--lyra-color-text))",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "white",
            fontWeight: 700,
            fontSize: Math.round(size * 0.28),
          }}
        >
          {initials}
        </div>
      )}
      {/* online dot */}
      <div
        style={{
          position: "absolute",
          right: Math.round(size * 0.07),
          bottom: Math.round(size * 0.07),
          width: Math.round(size * 0.15),
          height: Math.round(size * 0.15),
          borderRadius: "50%",
          background: "var(--lyra-color-success)",
          border: `${Math.round(size * 0.03)}px solid white`,
          boxShadow: "0 0 10px rgba(74,222,128,0.6)",
        }}
      />
    </div>
  );
}

/* ─── AgentCard ──────────────────────────────────────────────────── */
function AgentCard({ agent, onClone, cloned, isSelf }) {
  const [loading, setLoading] = useState(false);

  async function handleClone(e) {
    e.stopPropagation();
    try {
      setLoading(true);
      await onClone(agent._id);
    } catch (_) {}
    setLoading(false);
  }

  const isFlow = agent.type === "flow" || !!agent.canvasData;

  return (
    <div
      style={{
        borderRadius: 14,
        padding: "14px 16px",
        background: T.white,
        border: `1px solid ${T.border}`,
        transition: "all .18s ease",
        cursor: "pointer",
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.08)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = "none";
        e.currentTarget.style.boxShadow = "none";
      }}
    >
      {/* header row */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 12,
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            width: 34,
            height: 34,
            borderRadius: 10,
            background: T.greenLight,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 15,
          }}
        >
          {isFlow ? "⛓" : "🤖"}
        </div>
        <div
          style={{
            padding: "3px 8px",
            borderRadius: 999,
            background: "var(--lyra-color-surface-2)",
            color: "#047857",
            fontSize: 10,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          <span
            style={{
              width: 5,
              height: 5,
              borderRadius: "50%",
              background: "var(--lyra-color-success)",
              display: "inline-block",
            }}
          />
          ready
        </div>
      </div>

      {/* name */}
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: T.ink,
          marginBottom: 4,
          lineHeight: 1.3,
        }}
      >
        {agent.name}
      </div>

      {/* description / steps */}
      <div
        style={{ fontSize: 11, color: T.ink4, lineHeight: 1.55, minHeight: 28 }}
      >
        {agent.description ||
          (agent.steps?.length
            ? `${agent.steps.length} steps`
            : "AI workflow agent")}
      </div>

      {/* footer */}
      <div
        style={{
          marginTop: 12,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <div
          style={{
            fontSize: 11,
            color: T.ink6,
            display: "flex",
            alignItems: "center",
            gap: 4,
          }}
        >
          ⚙ {agent.skillsCount ?? agent.skills?.length ?? 0} skills
        </div>
        {!isSelf && (
          <button
            onClick={handleClone}
            disabled={loading || cloned}
            style={{
              border: "none",
              background: cloned ? "var(--lyra-color-success-bg)" : T.greenDark,
              color: cloned ? "var(--lyra-color-success)" : "white",
              padding: "6px 12px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {loading ? "…" : cloned ? "✓ Cloned" : "Clone"}
          </button>
        )}
      </div>
    </div>
  );
}

/* ─── LyranProfilePage ───────────────────────────────────────────── */
export default function LyranProfilePage() {
  const { userId } = useParams();
  const navigate = useNavigate();

  const currentUserId = getUserIdFromToken();
  const isSelf = currentUserId && String(currentUserId) === String(userId);

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);
  const [clonedMap, setClonedMap] = useState({});
  const [showAll, setShowAll] = useState(false);

  /* fetch profile + build already-cloned map */
  useEffect(() => {
    if (!userId) return;
    communityAPI
      .getMemberProfile(userId)
      .then(async (r) => {
        const prof = r.profile || null;
        setProfile(prof);
        setFollowing(prof?.isFollowing || false);
        try {
          const myRes = await customAgentAPI.list();
          const myAgents = myRes.agents || myRes || [];
          const map = {};
          myAgents.forEach((ma) => {
            const origin = ma.originMarketplaceId || ma.parentAgentId;
            if (origin) map[String(origin)] = true;
          });
          setClonedMap(map);
        } catch (_) {}
      })
      .finally(() => setLoading(false));
  }, [userId]);

  async function handleFollow() {
    try {
      const r = await communityAPI.toggleFollow(userId);
      setFollowing(r.following);
      setProfile((prev) => ({
        ...prev,
        followerCount: (prev.followerCount || 0) + (r.following ? 1 : -1),
      }));
    } catch (_) {}
  }

  async function handleClone(agentId) {
    await agentAPI.clone(agentId);
    setClonedMap((prev) => ({ ...prev, [agentId]: true }));
  }

  /* ── loading / empty states ── */
  if (loading)
    return (
      <div
        style={{
          height: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: T.ink4,
          fontFamily: "DM Sans, sans-serif",
        }}
      >
        Loading…
      </div>
    );
  if (!profile)
    return (
      <div
        style={{
          height: 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: T.ink4,
          fontFamily: "DM Sans, sans-serif",
        }}
      >
        Profile not found
      </div>
    );

  /* ── derived values ── */
  const agents = profile.publicAgents || [];
  const visibleAgents = showAll ? agents : agents.slice(0, 8);

  /* primary identity = lyran_name / lyran_avatar; fallback to real name/pic */
  const displayName = profile.lyran_name || profile.name;
  const avatarSrc = profile.lyran_avatar || profile.profilePicture || null;
  const realName = profile.name;

  /* ─────────────────────────────────────────────────────────────── */
  return (
    <div
      style={{
        minHeight: "100vh",
        background: T.bg,
        padding: "24px",
        fontFamily: "DM Sans, sans-serif",
      }}
    >
      <div
        style={{
          maxWidth: 900,
          margin: "0 auto",
          display: "flex",
          flexDirection: "column",
          gap: 20,
        }}
      >
        {/* HERO section commented out */}

        {/* ABOUT section commented out */}

        {/* ── LYRAN SECTION ── */}
        <div
          style={{
            borderRadius: 24,
            background: "linear-gradient(135deg,var(--lyra-color-surface) 0%,var(--lyra-color-surface-2) 100%)",
            padding: "30px 36px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 28,
            boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
            border: `1px solid ${T.border}`,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 26 }}>
            {/* orb */}
            <div
              style={{
                width: 106,
                height: 106,
                borderRadius: "50%",
                border: "1px dashed rgba(29,158,117,0.25)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                flexShrink: 0,
              }}
            >
              <div
                style={{
                  position: "absolute",
                  inset: 14,
                  borderRadius: "50%",
                  border: "1px dashed rgba(29,158,117,0.14)",
                }}
              />
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: "50%",
                  background: "linear-gradient(135deg,var(--lyra-color-success) 0%,var(--lyra-color-text) 100%)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "white",
                  fontSize: 22,
                  boxShadow: "0 10px 28px rgba(29,158,117,0.28)",
                }}
              >
                ✦
              </div>
            </div>

            <div>
              <div
                style={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: T.ink,
                  marginBottom: 5,
                  letterSpacing: "-0.03em",
                }}
              >
                {profile.lyran_name || displayName}
              </div>
              {/* SECONDARY: real username */}
                <div
                  style={{
                    fontSize: 12,
                    color: T.ink,
                    marginBottom: 14,
                  }}
                >
                  @{realName}
                </div>
              <div style={{ fontSize: 13, color: T.ink4, marginBottom: 14 }}>
                Your AI orchestration companion
              </div>
              <div
                style={{
                  fontSize: 13,
                  color: T.ink4,
                  lineHeight: 1.7,
                  maxWidth: 440,
                  marginBottom: 14,
                }}
              >
                Orchestrates your workflows, agents and tools quietly in the
                background.
              </div>
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  height: 28,
                  padding: "0 10px",
                  borderRadius: 999,
                  background: "var(--lyra-color-surface-2)",
                  color: T.green,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                <div
                  style={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    background: "var(--lyra-color-success)",
                  }}
                />
                Active
                <span style={{ opacity: 0.4 }}>•</span>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    color: T.ink4,
                    fontSize: 13,
                  }}
                >
                  <span>
                    {profile.followerCount || 0} follower
                    {profile.followerCount !== 1 ? "s" : ""}
                  </span>
                  <span style={{ opacity: 0.4 }}>•</span>
                  <span>{(profile.following || []).length} following</span>
                  <span style={{ opacity: 0.4 }}>•</span>
                  <span>{profile.agentsCount} agent{profile.agentsCount !== 1 ? "s" : ""}</span>
                </div>
              </div>
            </div>
          </div>

          <div>
              {/* follow button (hidden for self) */}
              {!isSelf && (
                <button
                  onClick={handleFollow}
                  style={{
                    height: 40,
                    padding: "0 18px",
                    borderRadius: 12,
                    flexShrink: 0,
                    border: "1px solid rgba(255,255,255,0.2)",
                    background: following
                      ? "#184C3A"
                      : "var(--lyra-color-success)",
                    backdropFilter: "blur(12px)",
                    color: "white",
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  {following ? (
                    <>
                      <span>✓</span> Following
                    </>
                  ) : (
                    <>
                      <span>+</span> Follow
                    </>
                  )}
                </button>
              )}

            {/* <button
              style={{
                height: 44,
                padding: "0 20px",
                borderRadius: 13,
                border: "none",
                background: "linear-gradient(135deg,var(--lyra-color-success) 0%,#184C3A 100%)",
                color: "white",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
                boxShadow: "0 8px 24px rgba(29,158,117,0.22)",
                whiteSpace: "nowrap",
                flexShrink: 0,
                display: "flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              Open Lyran Workspace <span style={{ fontSize: 15 }}>↗</span>
            </button> */}
          </div>
        </div>

        {/* ── PUBLIC AGENTS ── */}
        {agents.length > 0 && (
          <div
            style={{
              borderRadius: 24,
              background: T.white,
              padding: "32px 36px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.04)",
            }}
          >
            {/* section header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 22,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 11,
                    background: "var(--lyra-color-surface-2)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 17,
                  }}
                >
                  🤖
                </div>
                <div>
                  <div style={{ fontSize: 16, fontWeight: 600, color: T.ink }}>
                    Public Agents
                  </div>
                  <div style={{ fontSize: 12, color: T.ink4 }}>
                    Agents shared by {displayName} with the community
                  </div>
                </div>
              </div>
              <div
                style={{
                  padding: "4px 10px",
                  borderRadius: 999,
                  background: T.greenLight,
                  color: T.green,
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                {agents.length} agent{agents.length !== 1 ? "s" : ""}
              </div>
            </div>

            {/* grid */}
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(4,1fr)",
                gap: 14,
              }}
            >
              {visibleAgents.map((agent) => (
                <AgentCard
                  key={agent._id}
                  agent={agent}
                  onClone={handleClone}
                  cloned={!!clonedMap[String(agent._id)]}
                  isSelf={isSelf}
                />
              ))}
            </div>

            {/* view all */}
            {agents.length > 8 && (
              <div style={{ marginTop: 22, textAlign: "center" }}>
                <button
                  onClick={() => setShowAll((v) => !v)}
                  style={{
                    height: 38,
                    padding: "0 22px",
                    borderRadius: 11,
                    border: `1px solid ${T.border}`,
                    background: T.white,
                    color: T.ink2,
                    fontSize: 13,
                    fontWeight: 500,
                    cursor: "pointer",
                  }}
                >
                  {showAll ? "Show less" : "View all agents"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
